import { Color, DotLegalDeleteDialog, DotLegalMultiSelect } from "@dotlegal/dotlegal-ui-components";
import React from "react";
import { ContractModel } from "../Contract.types.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useContractResponsibilityHelper } from "../ContractResponsibilityHelper.hooks.ts";
import { useResponsibleQuery } from "../../../user/hooks/useResponsibleQuery.ts";
import { Trans } from "react-i18next";

export interface AdditionalAccessFieldProps {
    /** Lets a caller that owns the vertical rhythm itself turn the field's own margin off. */
    noMargin?: boolean;
    isLoading: boolean;
    contract: ContractModel | undefined;
    onChange: (changeFunc: (contract: ContractModel) => void) => Promise<void>;
    readOnly: boolean;
    required: boolean;
    isDisabled: boolean;
    isPreview: boolean;
    errorText?: string;
    disableAccessWarning?: boolean;
    showWarningAsInfo?: boolean;
    /** Suppresses the field's help icon, for a view where the same field is shown twice. */
    hideToolTip?: boolean;
}

function AdditionalAccessField(props: AdditionalAccessFieldProps) {
    const { translateString } = useTranslation();
    const { onFieldChange, handleDialogConfirm, pendingUserChanges, setPendingUserChanges } = useContractResponsibilityHelper(
        props.onChange,
        props.disableAccessWarning
    );

    const userQuery = useResponsibleQuery(!props.isPreview);

    return (
        <>
            <DotLegalMultiSelect
                noMargin={props.noMargin}
                options={(userQuery.dataByList(props.contract?.additionalAccessUsers ?? []) ?? [])
                    .filter((x) => !props.contract?.excludedUsers.includes(x.id))
                    .map((x) => ({ ...x, name: `${x.name}${x.active ? "" : ` (${translateString("deactivated")})`}` }))}
                isLoading={(userQuery.isLoading || props.isLoading) && !props.readOnly}
                onChange={
                    props.readOnly
                        ? () => {}
                        : (values) => {
                              let newContract = { ...props.contract!, additionalAccessUsers: values };
                              onFieldChange(newContract, props.contract!);
                          }
                }
                label={translateString("additionalUserAccess")}
                noOptionsLabel={translateString("noOptions")}
                warningText={props.isPreview || props.showWarningAsInfo ? undefined : translateString("contractAdditionalAccessWarning")}
                toolTipText={
                    props.hideToolTip
                        ? undefined
                        : props.showWarningAsInfo
                          ? `${translateString("contractAdditionalAccessTooltip")}\n\n${translateString("contractAdditionalAccessWarning")}`
                          : translateString("contractAdditionalAccessTooltip")
                }
                disableClearable={props.required}
                chosenOptions={props.contract?.additionalAccessUsers ?? []}
                disabled={props.isDisabled}
                errorText={props.errorText}
            />

            {pendingUserChanges && (
                <DotLegalDeleteDialog
                    open
                    headerText={translateString("confirmAccessRemoval")}
                    alertBodyText={
                        <Trans
                            i18nKey="confirmContractAccessRemovalBody"
                            components={{
                                ul: <ul />,
                                li: <li />,
                            }}
                        />
                    }
                    warningTranslation={translateString("warning")}
                    deleteBtnText={translateString("save")}
                    cancelBtnText={translateString("cancel")}
                    onDialogClose={() => setPendingUserChanges(null)}
                    onOkClick={() => handleDialogConfirm()}
                />
            )}
        </>
    );
}

export default AdditionalAccessField;
